import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import { catchError } from 'rxjs/operators';
import { throwError } from 'rxjs';
import i18next from 'i18next';
import type { AppDispatch, RootState } from './store';
import providerService from '../services/providerService';
import providerUtils from '../services/providerUtils';
import invoiceService from '../services/invoiceService';
import * as driverSlice from './driver.slice';
import * as vehicleSlice from './vehicle.slice';
import * as uiSlice from './ui.slice';
import type {
  BaseProvider,
  Provider,
  ProviderOptimized,
  Tier3Provider,
} from './models/provider.model';
import type {
  ProviderNote,
  ProviderNoteComment,
} from './models/provider-note.model';
import type { PagedList } from './models/paged-list.model';
import type { SortModel } from './models/sort-model.model';
import type {
  DocumentDownloadInfo,
  ProviderDocument,
} from './models/document.model';
import type { BaseTripPreferences } from './models/trip-preferences.model';
import type { CoverageArea } from './models/coverage-area.model';
import type {
  Tier3TripLegAttestation,
  TripLegsToAttest,
} from './models/trip-leg.model';

interface ProviderState {
  selectedFilter: string;
  searchFilter: string;
  searchDriverIdFilter: string;
  searchAreaFilter: string;
  driverSearchFilter: string;
  vehicleSearchFilter: string;
  page: number;
  pageSize: number;
  sortModel: SortModel | null;
  loadingProviderList: boolean;
  loadedProviderList: boolean;
  providerList: PagedList<ProviderOptimized>;
  tripPreferences: BaseTripPreferences | null;

  // tier3SelectedFilter: string;
  // tier3SearchFilter: string;
  // tier3Page: number;
  // tier3PageSize: number;
  // tier3SortModel: SortModel | null;
  // loadingTier3ProviderList: boolean;
  // loadedTier3ProviderList: boolean;
  // tier3ProviderList: PagedList<Tier3Provider>;
  // tier3Provider: Tier3Provider | null;

  loadingProvider: boolean;
  loadedProvider: boolean;
  loadingProviderError: string | null;
  savingProvider: boolean;
  savedProvider: boolean;
  savingError: string | null;
  savingSuccess: string | null;
  provider: BaseProvider | null;
  providerDocuments: ProviderDocument[];
  loadingProviderDocuments: boolean;
  uploadingDocument: boolean;
  uploadedDocument: boolean;
  uploadError: string | null;
  providerNotes: ProviderNote[];
  savingNote: boolean;
  savedNote: boolean;
  savingNoteError: string | null;
  loadingTripPreferences: boolean;
  loadingTripPreferencesError: string | null;
  savingTripPreferences: boolean;
  savedTripPreferences: boolean;
  savingTripPreferencesError: string | null;
  savingOnboardingUser: boolean;
  savedOnboardingUser: boolean;

  tabsetOneIndex: number;
  tabsetTwoIndex: number;

  //
  unInvitedSearchFilter: string;
  unInvitedPage: number;
  unInvitedPageSize: number;
  unInvitedSortModel: SortModel | null;

  coverageAreas: CoverageArea[];
  loadingCoverageAreas: boolean;
  loadedCoverageAreas: boolean;

  tripLegsToAttest: TripLegsToAttest | null;
  loadingTripLegsToAttest: boolean;
  loadedTripLegsToAttest: boolean;
  attestationQueue: string[];
  attestationError: string | null;
  submittingAttestation: boolean;
  submittedAttestation: boolean;
  submittingInvoice: boolean;
  submittedInvoice: boolean;

  bensonProviderID: number | null;

  documentDownloadInfo: DocumentDownloadInfo | null;
  loadingDocumentDownloadInfo: boolean;
  loadedDocumentDownloadInfo: boolean;
}

export const initialState: ProviderState = {
  selectedFilter: '',
  searchFilter: '',
  searchDriverIdFilter: '',
  searchAreaFilter: '',
  driverSearchFilter: '',
  vehicleSearchFilter: '',
  page: 1,
  pageSize: 10,
  sortModel: null,
  loadingProviderList: false,
  loadedProviderList: false,
  providerList: { itemCount: 0, currentPage: 1, totalPages: 0, items: [] },
  tripPreferences: null,

  // tier3SelectedFilter: '',
  // tier3SearchFilter: '',
  // tier3Page: 1,
  // tier3PageSize: 10,
  // tier3SortModel: null,
  // loadingTier3ProviderList: false,
  // loadedTier3ProviderList: false,
  // tier3ProviderList: { itemCount: 0, currentPage: 1, totalPages: 0, items: [] },
  // tier3Provider: null,

  loadingProvider: false,
  loadedProvider: false,
  loadingProviderError: null,
  savingProvider: false,
  savedProvider: false,
  savingError: null,
  savingSuccess: null,
  provider: null,
  providerDocuments: [],
  loadingProviderDocuments: false,
  uploadingDocument: false,
  uploadedDocument: false,
  uploadError: null,
  providerNotes: [],
  savingNote: false,
  savedNote: false,
  savingNoteError: null,
  loadingTripPreferences: false,
  loadingTripPreferencesError: null,
  savingTripPreferences: false,
  savedTripPreferences: false,
  savingTripPreferencesError: null,
  savingOnboardingUser: false,
  savedOnboardingUser: false,

  tabsetOneIndex: 0,
  tabsetTwoIndex: 0,
  //uninvited
  unInvitedSearchFilter: '',
  unInvitedPage: 1,
  unInvitedPageSize: 10,
  unInvitedSortModel: null,

  coverageAreas: [],
  loadingCoverageAreas: false,
  loadedCoverageAreas: false,

  tripLegsToAttest: null,
  loadingTripLegsToAttest: false,
  loadedTripLegsToAttest: false,
  attestationQueue: [],
  attestationError: null,
  submittingAttestation: false,
  submittedAttestation: false,
  submittingInvoice: false,
  submittedInvoice: false,

  bensonProviderID: null,

  documentDownloadInfo: null,
  loadingDocumentDownloadInfo: false,
  loadedDocumentDownloadInfo: false,
};

export const slice = createSlice({
  name: 'provider',
  initialState,
  reducers: {
    setSelectedFilter: (
      state: ProviderState,
      action: { type: string; payload: string },
    ) => {
      state.selectedFilter = action.payload;
      state.page = 1;
    },
    setSearchFilter: (
      state: ProviderState,
      action: { type: string; payload: string },
    ) => {
      state.searchFilter = action.payload;
      state.page = 1;
    },
    setSearchDriverIdFilter: (
      state: ProviderState,
      action: { type: string; payload: string },
    ) => {
      state.searchDriverIdFilter = action.payload;
      state.page = 1;
    },
    setSearchAreaFilter: (
      state: ProviderState,
      action: { type: string; payload: string },
    ) => {
      state.searchAreaFilter = action.payload;
      state.page = 1;
    },
    setDriverSearchFilter: (
      state: ProviderState,
      action: { type: string; payload: string },
    ) => {
      state.driverSearchFilter = action.payload;
      state.page = 1;
    },
    setVehicleSearchFilter: (
      state: ProviderState,
      action: { type: string; payload: string },
    ) => {
      state.vehicleSearchFilter = action.payload;
      state.page = 1;
    },
    setPage: (
      state: ProviderState,
      action: { type: string; payload: number },
    ) => {
      state.page = action.payload;
    },
    setPageSize: (
      state: ProviderState,
      action: { type: string; payload: number },
    ) => {
      state.pageSize = action.payload;
    },
    setSortModel: (
      state: ProviderState,
      action: PayloadAction<SortModel | null>,
    ) => {
      state.sortModel = action.payload;
    },
    setLoadingProviderList: (
      state: ProviderState,
      action: { type: string; payload: boolean },
    ) => {
      state.loadingProviderList = action.payload;
    },
    setLoadedProviderList: (
      state: ProviderState,
      action: { type: string; payload: boolean },
    ) => {
      state.loadedProviderList = action.payload;
    },
    setProviderList: (
      state: ProviderState,
      action: { type: string; payload: PagedList<ProviderOptimized> },
    ) => {
      state.loadingProviderList = false;
      state.loadedProviderList = true;
      state.providerList = action.payload;
    },

    // setTier3SelectedFilter: (
    //   state: ProviderState,
    //   action: { type: string; payload: string },
    // ) => {
    //   state.tier3SelectedFilter = action.payload;
    //   state.tier3Page = 1;
    // },
    // setTier3SearchFilter: (
    //   state: ProviderState,
    //   action: { type: string; payload: string },
    // ) => {
    //   state.tier3SearchFilter = action.payload;
    //   state.tier3Page = 1;
    // },
    // setTier3Page: (
    //   state: ProviderState,
    //   action: { type: string; payload: number },
    // ) => {
    //   state.tier3Page = action.payload;
    // },
    // setTier3PageSize: (
    //   state: ProviderState,
    //   action: { type: string; payload: number },
    // ) => {
    //   state.tier3PageSize = action.payload;
    // },
    // setTier3SortModel: (
    //   state: ProviderState,
    //   action: PayloadAction<SortModel | null>,
    // ) => {
    //   state.tier3SortModel = action.payload;
    // },
    // setLoadingTier3ProviderList: (
    //   state: ProviderState,
    //   action: { type: string; payload: boolean },
    // ) => {
    //   state.loadingTier3ProviderList = action.payload;
    // },
    // setLoadedTier3ProviderList: (
    //   state: ProviderState,
    //   action: { type: string; payload: boolean },
    // ) => {
    //   state.loadedTier3ProviderList = action.payload;
    // },
    // setTier3ProviderList: (
    //   state: ProviderState,
    //   action: { type: string; payload: PagedList<Tier3Provider> },
    // ) => {
    //   state.loadingTier3ProviderList = false;
    //   state.loadedTier3ProviderList = true;
    //   state.tier3ProviderList = action.payload;
    // },
    // addTier3Provider: (
    //   state: ProviderState,
    //   action: { type: string; payload: Tier3Provider },
    // ) => {
    //   state.tier3ProviderList.items.unshift(action.payload);
    //   state.tier3ProviderList.itemCount = state.tier3ProviderList.itemCount + 1;
    //   state.tier3Provider = action.payload;
    // },
    // updateTier3Provider: (
    //   state: ProviderState,
    //   action: { type: string; payload: Tier3Provider },
    // ) => {
    //   const idx = state.tier3ProviderList.items.findIndex(
    //     (p) =>
    //       p.transportationProviderID ===
    //       action.payload.transportationProviderID,
    //   );
    //   if (idx >= 0) {
    //     state.tier3ProviderList.items.splice(idx, 1, action.payload);
    //   }
    //   state.tier3Provider = action.payload;
    // },
    setLoadingProvider: (
      state: ProviderState,
      action: { type: string; payload: boolean },
    ) => {
      state.loadingProvider = action.payload;
    },
    setLoadedProvider: (
      state: ProviderState,
      action: { type: string; payload: boolean },
    ) => {
      state.loadedProvider = action.payload;
    },
    setLoadingProviderError: (
      state: ProviderState,
      action: { type: string; payload: string | null },
    ) => {
      state.loadingProviderError = action.payload;
    },
    // setTier3Provider: (
    //   state: ProviderState,
    //   action: { type: string; payload: Tier3Provider | null },
    // ) => {
    //   state.tier3Provider = action.payload;
    // },

    setSavingProvider: (
      state: ProviderState,
      action: { type: string; payload: boolean },
    ) => {
      state.savingProvider = action.payload;
    },
    setSavedProvider: (
      state: ProviderState,
      action: { type: string; payload: boolean },
    ) => {
      state.savedProvider = action.payload;
    },
    setSavingError: (
      state: ProviderState,
      action: { type: string; payload: string | null },
    ) => {
      state.savingError = action.payload;
    },
    setSavingSuccess: (
      state: ProviderState,
      action: { type: string; payload: string | null },
    ) => {
      state.savingSuccess = action.payload;
    },
    resetSavingFlags: (state: ProviderState, action: { type: string }) => {
      state.savingProvider = false;
      state.savedProvider = false;
      state.savingError = null;

      state.savingTripPreferences = false;
      state.savedTripPreferences = false;
      state.savingTripPreferencesError = null;
    },
    setProvider: (
      state: ProviderState,
      action: { type: string; payload: BaseProvider | null },
    ) => {
      state.provider = action.payload;
    },
    updateProviderInList: (
      state: ProviderState,
      action: { type: string; payload: ProviderOptimized },
    ) => {
      const providers = [...state.providerList.items];
      const idx = providers.findIndex(
        (p) =>
          p.transportationProviderID ===
          action.payload.transportationProviderID,
      );
      if (idx >= 0) {
        providers.splice(idx, 1, action.payload);
        state.providerList.items = providers;
      }
    },
    setProviderDocuments: (
      state: ProviderState,
      action: { type: string; payload: ProviderDocument[] },
    ) => {
      state.providerDocuments = action.payload;
    },
    setLoadingProviderDocuments: (
      state: ProviderState,
      action: { type: string; payload: boolean },
    ) => {
      state.loadingProviderDocuments = action.payload;
    },
    setUploadingDocument: (
      state: ProviderState,
      action: { type: string; payload: boolean },
    ) => {
      state.uploadingDocument = action.payload;
    },
    setUploadedDocument: (
      state: ProviderState,
      action: { type: string; payload: boolean },
    ) => {
      state.uploadedDocument = action.payload;
    },
    setUploadError: (
      state: ProviderState,
      action: { type: string; payload: string | null },
    ) => {
      state.uploadError = action.payload;
    },
    addProviderDocument: (
      state: ProviderState,
      action: { type: string; payload: ProviderDocument },
    ) => {
      state.providerDocuments = [...state.providerDocuments, action.payload];
    },
    updateProviderDocument: (
      state: ProviderState,
      action: { type: string; payload: ProviderDocument },
    ) => {
      const docs = [...state.providerDocuments];
      const idx = docs.findIndex(
        (d) =>
          d.transportationProviderDocumentID ===
          action.payload.transportationProviderDocumentID,
      );
      if (idx >= 0) {
        docs.splice(idx, 1, action.payload);
        state.providerDocuments = docs;
      }
    },
    deleteProviderDocument: (
      state: ProviderState,
      action: { type: string; payload: number },
    ) => {
      const docs = [...state.providerDocuments];
      const idx = docs.findIndex(
        (d) => d.transportationProviderDocumentID === action.payload,
      );
      if (idx >= 0) {
        docs.splice(idx, 1);
        state.providerDocuments = docs;
      }
    },
    setSavingNote: (
      state: ProviderState,
      action: { type: string; payload: boolean },
    ) => {
      state.savingNote = action.payload;
    },
    setSavedNote: (
      state: ProviderState,
      action: { type: string; payload: boolean },
    ) => {
      state.savedNote = action.payload;
    },
    setSavingNoteError: (
      state: ProviderState,
      action: { type: string; payload: string | null },
    ) => {
      state.savingNoteError = action.payload;
    },
    resetSavingNoteFlags: (state: ProviderState, action: { type: string }) => {
      state.savingNote = false;
      state.savedNote = false;
      state.savingNoteError = null;
    },
    setProviderNotes: (
      state: ProviderState,
      action: { type: string; payload: ProviderNote[] },
    ) => {
      state.providerNotes = action.payload;
    },
    addProviderNote: (
      state: ProviderState,
      action: {
        type: string;
        payload: ProviderNote;
      },
    ) => {
      state.providerNotes = [action.payload, ...state.providerNotes];
    },
    updateProviderNote: (
      state: ProviderState,
      action: {
        type: string;
        payload: ProviderNote;
      },
    ) => {
      const notes = [...state.providerNotes];
      const idx = notes.findIndex(
        (d) =>
          d.transportationProviderNoteID ===
          action.payload.transportationProviderNoteID,
      );
      const commentCount = state.providerNotes[idx].commentCount;
      const comments =
        state.providerNotes[idx].transportationProviderNoteComments;
      if (idx >= 0) {
        notes.splice(idx, 1, {
          ...action.payload,
          commentCount: commentCount,
          transportationProviderNoteComments: comments,
        });
        state.providerNotes = notes;
      }
    },
    deleteProviderNote: (
      state: ProviderState,
      action: {
        type: string;
        payload: { providerId: number; noteId: number };
      },
    ) => {
      const docs = [...state.providerNotes];
      const idx = docs.findIndex(
        (d) => d.transportationProviderNoteID === action.payload.noteId,
      );
      if (idx >= 0) {
        docs.splice(idx, 1);
        state.providerNotes = docs;
      }
    },
    setNoteComments: (
      state: ProviderState,
      action: {
        type: string;
        payload: { noteId: number; comments: ProviderNoteComment[] };
      },
    ) => {
      const notes = [...state.providerNotes];
      const noteIdx = notes.findIndex(
        (n) => n.transportationProviderNoteID === action.payload.noteId,
      );
      notes[noteIdx] = {
        ...notes[noteIdx],
        transportationProviderNoteComments: action.payload.comments,
      };
      state.providerNotes = notes;
    },
    addNoteComment: (
      state: ProviderState,
      action: {
        type: string;
        payload: ProviderNoteComment;
      },
    ) => {
      const notes = [...state.providerNotes];
      const noteIdx = notes.findIndex(
        (n) =>
          n.transportationProviderNoteID ===
          action.payload.transportationProviderNoteID,
      );
      const comments = notes[noteIdx].transportationProviderNoteComments || [];
      notes[noteIdx] = {
        ...notes[noteIdx],
        commentCount: comments.length + 1,
        transportationProviderNoteComments: [...comments, action.payload],
      };
      state.providerNotes = notes;
    },
    updateNoteComment: (
      state: ProviderState,
      action: {
        type: string;
        payload: ProviderNoteComment;
      },
    ) => {
      const notes = [...state.providerNotes];
      const noteIdx = notes.findIndex(
        (n) =>
          n.transportationProviderNoteID ===
          action.payload.transportationProviderNoteID,
      );
      const comments = notes[noteIdx].transportationProviderNoteComments || [];
      const commentIdx = comments.findIndex(
        (c) =>
          c.transportationProviderNoteCommentID ===
          action.payload.transportationProviderNoteCommentID,
      );
      comments[commentIdx] = action.payload;
      notes[noteIdx] = {
        ...notes[noteIdx],
        transportationProviderNoteComments: [...comments],
      };
      state.providerNotes = notes;
    },
    deleteNoteComment: (
      state: ProviderState,
      action: {
        type: string;
        payload: { providerId: number; noteId: number; commentId: number };
      },
    ) => {
      const notes = [...state.providerNotes];
      const noteIdx = notes.findIndex(
        (n) => n.transportationProviderNoteID === action.payload.noteId,
      );
      const comments = notes[noteIdx].transportationProviderNoteComments || [];
      const commentIdx = comments.findIndex(
        (c) =>
          c.transportationProviderNoteCommentID === action.payload.commentId,
      );
      comments.splice(commentIdx, 1);
      notes[noteIdx] = {
        ...notes[noteIdx],
        commentCount: comments.length,
        transportationProviderNoteComments: [...comments],
      };
      state.providerNotes = notes;
    },
    setTabsetOneIndex: (
      state: ProviderState,
      action: { type: string; payload: number },
    ) => {
      state.tabsetOneIndex = action.payload;
    },
    setTabsetTwoIndex: (
      state: ProviderState,
      action: { type: string; payload: number },
    ) => {
      state.tabsetTwoIndex = action.payload;
    },
    setLoadingTripPreferences: (
      state: ProviderState,
      action: { type: string; payload: boolean },
    ) => {
      state.loadingTripPreferences = action.payload;
    },
    setLoadingTripPreferencesError: (
      state: ProviderState,
      action: { type: string; payload: string | null },
    ) => {
      state.loadingTripPreferencesError = action.payload;
    },
    setTripPreferences: (
      state: ProviderState,
      action: { type: string; payload: BaseTripPreferences | null },
    ) => {
      state.tripPreferences = action.payload;
      if (state.provider) {
        state.provider = {
          ...state.provider,
          coverageAreas: action.payload?.coverageAreas,
        };
      }
    },
    setSavingTripPreferences: (
      state: ProviderState,
      action: { type: string; payload: boolean },
    ) => {
      state.savingTripPreferences = action.payload;
    },
    setSavedTripPreferences: (
      state: ProviderState,
      action: { type: string; payload: boolean },
    ) => {
      state.savedTripPreferences = action.payload;
    },
    setSavingTripPreferencesError: (
      state: ProviderState,
      action: { type: string; payload: string | null },
    ) => {
      state.savingTripPreferencesError = action.payload;
    },
    setSavingOnboardingUser: (
      state: ProviderState,
      action: { type: string; payload: boolean },
    ) => {
      state.savingOnboardingUser = action.payload;
    },
    setSavedOnboardingUser: (
      state: ProviderState,
      action: { type: string; payload: boolean },
    ) => {
      state.savedOnboardingUser = action.payload;
    },

    //
    setUninvitedSearchFilter: (
      state: ProviderState,
      action: { payload: string },
    ) => {
      state.unInvitedSearchFilter = action.payload;
      state.unInvitedPage = 1;
    },
    setUninvitedPage: (state: ProviderState, action: { payload: number }) => {
      state.unInvitedPage = action.payload;
    },
    setUninvitedPageSize: (
      state: ProviderState,
      action: { payload: number },
    ) => {
      state.unInvitedPageSize = action.payload;
    },
    setUninvitedSortModel: (
      state: ProviderState,
      action: { payload: SortModel | null },
    ) => {
      state.unInvitedSortModel = action.payload;
    },
    setCoverageAreas: (
      state: ProviderState,
      action: { payload: CoverageArea[] },
    ) => {
      state.coverageAreas = action.payload;
    },
    setLoadingCoverageAreas: (
      state: ProviderState,
      action: { type: string; payload: boolean },
    ) => {
      state.loadingCoverageAreas = action.payload;
    },
    setLoadedCoverageAreas: (
      state: ProviderState,
      action: { type: string; payload: boolean },
    ) => {
      state.loadedCoverageAreas = action.payload;
    },
    setTripLegsToAttest: (
      state: ProviderState,
      action: { payload: TripLegsToAttest },
    ) => {
      const existingTripLegs = state.tripLegsToAttest?.items || [];
      if (existingTripLegs.length === 0) {
        state.tripLegsToAttest = action.payload;
      } else {
        state.tripLegsToAttest = {
          ...action.payload,
          items: [
            ...existingTripLegs,
            ...action.payload.items.filter(
              (item) =>
                existingTripLegs.findIndex(
                  (existingItem) =>
                    existingItem.tripLegReferenceID === item.tripLegReferenceID,
                ) === -1,
            ),
          ],
        };
      }
    },
    setLoadingTripLegsToAttest: (
      state: ProviderState,
      action: { type: string; payload: boolean },
    ) => {
      state.loadingTripLegsToAttest = action.payload;
    },
    setLoadedTripLegsToAttest: (
      state: ProviderState,
      action: { type: string; payload: boolean },
    ) => {
      state.loadedTripLegsToAttest = action.payload;
    },
    removeAttestedTripLeg: (
      state: ProviderState,
      action: { payload: string },
    ) => {
      const legs = [...(state.tripLegsToAttest?.items || [])];
      const idx = legs.findIndex(
        (l) => l.tripLegReferenceID === action.payload,
      );
      if (idx >= 0) {
        legs.splice(idx, 1);
        state.tripLegsToAttest!.items = legs;
      }
    },
    setAttestationError: (
      state: ProviderState,
      action: { payload: string | null },
    ) => {
      state.attestationError = action.payload;
    },
    addAttestationToQueue: (
      state: ProviderState,
      action: { payload: string },
    ) => {
      state.attestationQueue.push(action.payload);
      state.submittingAttestation = true;
    },
    removeAttestationFromQueue: (
      state: ProviderState,
      action: { payload: string },
    ) => {
      state.attestationQueue = state.attestationQueue.filter(
        (id) => id !== action.payload,
      );
      if (state.attestationQueue.length === 0) {
        state.submittingAttestation = false;
        state.submittedAttestation = true;
      }
    },
    clearAttestationQueue: (state: ProviderState) => {
      state.attestationQueue = [];
      state.submittingAttestation = false;
      state.submittedAttestation = false;
    },
    setSubmittingAttestation: (
      state: ProviderState,
      action: { payload: boolean },
    ) => {
      state.submittingAttestation = action.payload;
    },
    setSubmittedAttestation: (
      state: ProviderState,
      action: { payload: boolean },
    ) => {
      state.submittedAttestation = action.payload;
    },
    setSubmittingInvoice: (
      state: ProviderState,
      action: { payload: boolean },
    ) => {
      state.submittingInvoice = action.payload;
    },
    setSubmittedInvoice: (
      state: ProviderState,
      action: { payload: boolean },
    ) => {
      state.submittedInvoice = action.payload;
    },
    setBensonProviderID: (
      state: ProviderState,
      action: { payload: number },
    ) => {
      state.bensonProviderID = action.payload;
    },
    setDocumentDownloadInfo: (
      state: ProviderState,
      action: { payload: DocumentDownloadInfo | null },
    ) => {
      state.documentDownloadInfo = action.payload;
    },
    setLoadingDocumentDownloadInfo: (
      state: ProviderState,
      action: { payload: boolean },
    ) => {
      state.loadingDocumentDownloadInfo = action.payload;
    },
    setLoadedDocumentDownloadInfo: (
      state: ProviderState,
      action: { payload: boolean },
    ) => {
      state.loadedDocumentDownloadInfo = action.payload;
    },
  },
});

//
export const { setUninvitedSearchFilter } = slice.actions;
export const { setUninvitedPage } = slice.actions;
export const { setUninvitedPageSize } = slice.actions;
export const { setUninvitedSortModel } = slice.actions;
//

export const { setSelectedFilter } = slice.actions;
export const { setSearchFilter } = slice.actions;
export const { setSearchDriverIdFilter } = slice.actions;
export const { setSearchAreaFilter } = slice.actions;
export const { setDriverSearchFilter } = slice.actions;
export const { setVehicleSearchFilter } = slice.actions;
export const { setPage } = slice.actions;
export const { setPageSize } = slice.actions;
export const { setSortModel } = slice.actions;
export const { setLoadingProviderList } = slice.actions;
export const { setLoadedProviderList } = slice.actions;
export const { setProviderList } = slice.actions;

// export const { setTier3SelectedFilter } = slice.actions;
// export const { setTier3SearchFilter } = slice.actions;
// export const { setTier3Page } = slice.actions;
// export const { setTier3PageSize } = slice.actions;
// export const { setTier3SortModel } = slice.actions;
// export const { setLoadingTier3ProviderList } = slice.actions;
// export const { setLoadedTier3ProviderList } = slice.actions;
// export const { setTier3ProviderList } = slice.actions;
// export const { setTier3Provider } = slice.actions;
// export const { addTier3Provider } = slice.actions;
// export const { updateTier3Provider } = slice.actions;

export const { setLoadingProvider } = slice.actions;
export const { setLoadedProvider } = slice.actions;
export const { setLoadingProviderError } = slice.actions;
export const { setSavingProvider } = slice.actions;
export const { setSavedProvider } = slice.actions;
export const { setSavingError } = slice.actions;
export const { setSavingSuccess } = slice.actions;
export const { resetSavingFlags } = slice.actions;
export const { setProvider } = slice.actions;
export const { updateProviderInList } = slice.actions;
export const { setProviderDocuments } = slice.actions;
export const { setLoadingProviderDocuments } = slice.actions;
export const { addProviderDocument } = slice.actions;
export const { updateProviderDocument } = slice.actions;
export const { deleteProviderDocument } = slice.actions;
export const { setUploadingDocument } = slice.actions;
export const { setUploadedDocument } = slice.actions;
export const { setUploadError } = slice.actions;
export const { setProviderNotes } = slice.actions;
export const { setSavingNote } = slice.actions;
export const { setSavedNote } = slice.actions;
export const { addProviderNote } = slice.actions;
export const { updateProviderNote } = slice.actions;
export const { deleteProviderNote } = slice.actions;
export const { setNoteComments } = slice.actions;
export const { addNoteComment } = slice.actions;
export const { updateNoteComment } = slice.actions;
export const { deleteNoteComment } = slice.actions;
export const { setSavingNoteError } = slice.actions;
export const { resetSavingNoteFlags } = slice.actions;
export const { setTabsetOneIndex } = slice.actions;
export const { setTabsetTwoIndex } = slice.actions;
export const {
  setLoadingTripPreferences,
  setLoadingTripPreferencesError,
  setSavingTripPreferences,
  setSavedTripPreferences,
  setSavingTripPreferencesError,
  setTripPreferences,
} = slice.actions;
export const { setSavingOnboardingUser, setSavedOnboardingUser } =
  slice.actions;
export const { setCoverageAreas } = slice.actions;
export const { setLoadingCoverageAreas } = slice.actions;
export const { setLoadedCoverageAreas } = slice.actions;
export const { setTripLegsToAttest } = slice.actions;
export const { setLoadingTripLegsToAttest } = slice.actions;
export const { setLoadedTripLegsToAttest } = slice.actions;
export const { removeAttestedTripLeg } = slice.actions;
export const { setAttestationError } = slice.actions;
export const { addAttestationToQueue } = slice.actions;
export const { removeAttestationFromQueue } = slice.actions;
export const { clearAttestationQueue } = slice.actions;
export const { setSubmittingAttestation } = slice.actions;
export const { setSubmittedAttestation } = slice.actions;
export const { setSubmittingInvoice } = slice.actions;
export const { setSubmittedInvoice } = slice.actions;
export const { setBensonProviderID } = slice.actions;
export const { setDocumentDownloadInfo } = slice.actions;
export const { setLoadingDocumentDownloadInfo } = slice.actions;
export const { setLoadedDocumentDownloadInfo } = slice.actions;
//

export const selectUninvitedSearchFilter = (state: {
  provider: ProviderState;
}) => state.provider.unInvitedSearchFilter;
export const selectUninvitedPage = (state: { provider: ProviderState }) =>
  state.provider.unInvitedPage;
export const selectUninvitedPageSize = (state: { provider: ProviderState }) =>
  state.provider.unInvitedPageSize;
export const selectUninvitedSortModel = (state: { provider: ProviderState }) =>
  state.provider.unInvitedSortModel;

//

export const fetchProviderListAsync =
  (
    pageSize: number,
    page: number,
    filter: string,
    search: string,
    sortModel: SortModel | null,
    tripModeIDs?: number[],
  ) =>
  (dispatch: AppDispatch) => {
    dispatch(setLoadingProviderList(true));
    dispatch(setLoadedProviderList(false));
    providerService
      .findProviders(pageSize, page, filter, search, sortModel, tripModeIDs)
      .subscribe((providerList) => {
        dispatch(setProviderList(providerList));
        dispatch(setLoadingProviderList(false));
        dispatch(setLoadedProviderList(true));
      });
  };

export const refetchProviderListAsync =
  () => (dispatch: AppDispatch, getState: () => RootState) => {
    const state = getState();
    const providerState = state.provider;
    dispatch(
      fetchProviderListAsync(
        providerState.pageSize,
        providerState.page,
        providerState.selectedFilter,
        providerState.searchFilter,
        providerState.sortModel,
        undefined,
      ),
    );
  };

// export const refetchTier3ProviderListAsync =
//   () => (dispatch: AppDispatch, getState: Function) => {
//     const state = getState();
//     const providerState = state.provider;
//     dispatch(
//       fetchTier3ProviderListAsync(
//         providerState.tier3PageSize,
//         providerState.tier3Page,
//         providerState.tier3SelectedFilter,
//         providerState.tier3SearchFilter,
//         providerState.tier3SortModel,
//       ),
//     );
//   };

// export const fetchTier3ProviderListAsync =
//   (
//     pageSize: number,
//     page: number,
//     filter: string,
//     search: string,
//     sortModel: SortModel | null,
//   ) =>
//   (dispatch: AppDispatch) => {
//     dispatch(setLoadingTier3ProviderList(true));
//     dispatch(setLoadedTier3ProviderList(false));
//     providerService
//       .findTier3Providers(pageSize, page, filter, search, sortModel)
//       .subscribe((providerList) => {
//         dispatch(setTier3ProviderList(providerList));
//         dispatch(setLoadingTier3ProviderList(false));
//         dispatch(setLoadedTier3ProviderList(true));
//       });
//   };

export const getProviderAsync = (id: number) => (dispatch: AppDispatch) => {
  dispatch(setProvider(null));
  dispatch(setLoadingProvider(true));
  dispatch(setLoadedProvider(false));
  dispatch(setLoadingProviderError(null));
  providerService
    .getProvider(id)
    .pipe(
      catchError((err) => {
        dispatch(setLoadingProvider(false));
        dispatch(setLoadedProvider(false));
        dispatch(setLoadingProviderError(err.message));
        return throwError(() => new Error(err.message));
      }),
    )
    .subscribe((provider) => {
      dispatch(setProvider(provider));
      dispatch(setLoadingProvider(false));
      dispatch(setLoadedProvider(true));
    });
};

// export const getTier3ProviderAsync = (id: number) => (dispatch: AppDispatch) => {
//   dispatch(setTier3Provider(null));
//   dispatch(setLoadingProvider(true));
//   dispatch(setLoadedProvider(false));
//   dispatch(setLoadingProviderError(null));
//   providerService
//     .getTier3Provider(id)
//     .pipe(
//       catchError((err) => {
//         dispatch(setLoadingProvider(false));
//         dispatch(setLoadedProvider(false));
//         dispatch(setLoadingProviderError(err.message));
//         return throwError(() => new Error(err.message));
//       }),
//     )
//     .subscribe((provider) => {
//       dispatch(setTier3Provider(provider));
//       dispatch(setLoadingProvider(false));
//       dispatch(setLoadedProvider(true));
//     });
// };

const handleSaveProviderSuccess = (
  dispatch: AppDispatch,
  provider: Provider,
) => {
  const optimizedProvider = providerUtils.convertToOptimized(provider);
  dispatch(updateProviderInList(optimizedProvider));
  dispatch(setProvider(provider));
  dispatch(setSavingProvider(false));
  dispatch(setSavedProvider(true));
  if (provider.isReadyClearToTransport) {
    dispatch(setSavingSuccess(i18next.t('common:provider.underReview')));
  }
};

const handleSaveTier3ProviderSuccess = (
  dispatch: AppDispatch,
  provider: Tier3Provider,
  isNew: boolean,
) => {
  dispatch(setProvider(provider));
  dispatch(setSavingProvider(false));
  dispatch(setSavedProvider(true));
};

const handleSaveProviderError = (dispatch: AppDispatch, error: any) => {
  dispatch(setSavingProvider(false));
  dispatch(setSavedProvider(false));
  let msg: string;
  if (error.messageId === 'DUPLICATE_ACCOUNT_NUMBER') {
    msg = i18next.t('common:provider.duplicateAccountNumber');
  } else {
    msg = i18next.t('common:provider.failedToSave');
  }
  const detail = error.message ?? (typeof error === 'string' ? error : null);
  dispatch(setSavingError(detail ? `${msg} - ${detail}` : msg));
};

export const createProviderAsync =
  (provider: Provider) => (dispatch: AppDispatch) => {
    dispatch(setProvider(null));
    dispatch(setSavingProvider(true));
    dispatch(setSavedProvider(false));
    dispatch(setSavingError(null));
    dispatch(setSavingSuccess(null));

    providerService
      .createProvider(provider)
      .pipe(
        catchError((err) => {
          handleSaveProviderError(dispatch, err);
          return throwError(() => new Error(err.message));
        }),
      )
      .subscribe((createdProvider) => {
        handleSaveProviderSuccess(dispatch, createdProvider);
      });
  };

export const updateProviderAsync =
  (provider: Provider | Partial<Provider>) => (dispatch: AppDispatch) => {
    dispatch(setSavingProvider(true));
    dispatch(setSavedProvider(false));
    dispatch(setSavingError(null));
    dispatch(setSavingSuccess(null));
    providerService
      .updateProvider(provider)
      .pipe(
        catchError((err) => {
          handleSaveProviderError(dispatch, err);
          return throwError(() => new Error(err.message));
        }),
      )
      .subscribe((updatedProvider) => {
        handleSaveProviderSuccess(dispatch, updatedProvider);
        dispatch(
          driverSlice.refetchDriverListAsync(
            provider.transportationProviderID!,
          ),
        );
        dispatch(
          vehicleSlice.refetchVehicleListAsync(
            provider.transportationProviderID!,
          ),
        );
      });
  };

export const createTier3ProviderAsync =
  (provider: Tier3Provider) => (dispatch: AppDispatch) => {
    // dispatch(setTier3Provider(null));
    dispatch(setSavingProvider(true));
    dispatch(setSavedProvider(false));
    dispatch(setSavingError(null));
    dispatch(setSavingSuccess(null));

    providerService
      .createTier3Provider(provider)
      .pipe(
        catchError((err) => {
          handleSaveProviderError(dispatch, err);
          return throwError(() => new Error(err.message));
        }),
      )
      .subscribe((createdProvider) => {
        handleSaveTier3ProviderSuccess(dispatch, createdProvider, true);
      });
  };

export const updateTier3ProviderAsync =
  (provider: Tier3Provider) => (dispatch: AppDispatch) => {
    dispatch(setSavingProvider(true));
    dispatch(setSavedProvider(false));
    dispatch(setSavingError(null));
    dispatch(setSavingSuccess(null));
    providerService
      .updateTier3Provider(provider)
      .pipe(
        catchError((err) => {
          handleSaveProviderError(dispatch, err);
          return throwError(() => new Error(err.message));
        }),
      )
      .subscribe((updatedProvider) => {
        handleSaveTier3ProviderSuccess(dispatch, updatedProvider, false);
      });
  };

export const deleteProviderAsync =
  (providerId: number) => (dispatch: AppDispatch) => {
    providerService
      .deleteProvider(providerId)
      .pipe(
        catchError((err) => {
          // This should be localized from messageId
          dispatch(setSavingError(err.message));
          return throwError(() => new Error(err.message));
        }),
      )
      .subscribe(() => {
        dispatch(refetchProviderListAsync());
      });
  };

// export const deleteTier3ProviderAsync =
//   (providerId: number) => (dispatch: AppDispatch) => {
//     providerService
//       .deleteTier3Provider(providerId)
//       .pipe(
//         catchError((err) => {
//           // This should be localized from messageId
//           dispatch(setSavingError(err.message));
//           return throwError(() => new Error(err.message));
//         }),
//       )
//       .subscribe(() => {
//         dispatch(refetchTier3ProviderListAsync());
//       });
//   };

export const uploadProviderDocumentAsync =
  (providerID: number, file: any, document: ProviderDocument) =>
  (dispatch: AppDispatch) => {
    dispatch(setUploadingDocument(true));
    dispatch(setUploadedDocument(false));
    dispatch(setUploadError(null));
    providerService
      .uploadProviderDocument(providerID, document, file)
      .subscribe({
        next: (doc) => {
          dispatch(setUploadingDocument(false));
          dispatch(setUploadedDocument(true));
          dispatch(addProviderDocument(doc));
        },
        error: (err) => {
          dispatch(setUploadingDocument(false));
          dispatch(setUploadedDocument(false));
          dispatch(setUploadError('common.documentTooLarge'));
        },
      });
  };

export const updateProviderDocumentAsync =
  (providerID: number, document: ProviderDocument) =>
  (dispatch: AppDispatch) => {
    dispatch(setUploadingDocument(true));
    dispatch(setUploadedDocument(false));
    dispatch(setUploadError(null));
    providerService
      .updateProviderDocument(providerID, document)
      .pipe(
        catchError((err) => {
          dispatch(setUploadingDocument(false));
          dispatch(setUploadedDocument(false));
          dispatch(setUploadError(err.message));
          return throwError(() => new Error(err.message));
        }),
      )
      .subscribe((doc) => {
        dispatch(setUploadingDocument(false));
        dispatch(setUploadedDocument(true));
        dispatch(updateProviderDocument(doc));
      });
  };

export const deleteProviderDocumentAsync =
  (providerId: number, documentId: number) => (dispatch: AppDispatch) => {
    providerService
      .deleteProviderDocument(providerId, documentId)
      .pipe(
        catchError((err) => {
          dispatch(setUploadError(err.message));
          return throwError(() => new Error(err.message));
        }),
      )
      .subscribe(() => {
        dispatch(deleteProviderDocument(documentId));
      });
  };

export const fetchProviderDocumentsAsync =
  (providerId: number) => (dispatch: AppDispatch) => {
    dispatch(setLoadingProviderDocuments(true));
    providerService.getProviderDocuments(providerId).subscribe({
      next: (documents) => {
        dispatch(setProviderDocuments(documents));
        dispatch(setLoadingProviderDocuments(false));
      },
      error: () => {
        dispatch(setLoadingProviderDocuments(false));
      },
    });
  };

export const fetchProviderNotesAsync =
  (providerId: number) => (dispatch: AppDispatch) => {
    providerService
      .getProviderNotes(providerId)
      // .pipe(
      //   catchError((err) => {
      //     // This should be localized from messageId
      //     dispatch(setSavingError(err.message));
      //     return throwError(() => new Error(err.message));
      //   }),
      // )
      .subscribe((notes) => {
        dispatch(setProviderNotes(notes));
      });
  };

export const createNoteAsync =
  (providerId: number, note: ProviderNote) =>
  (dispatch: AppDispatch, getState: () => RootState) => {
    dispatch(setSavingNote(true));
    dispatch(setSavedNote(false));
    dispatch(setSavingNoteError(null));
    providerService
      .createProviderNote(providerId, note)
      .pipe(
        catchError((err) => {
          dispatch(setSavingNote(false));
          dispatch(setSavedNote(false));
          dispatch(setSavingNoteError(err.message));
          return throwError(() => new Error(err.message));
        }),
      )
      .subscribe((createdNote) => {
        dispatch(setSavingNote(false));
        dispatch(setSavedNote(true));
        try {
          const userInfo = (getState() as RootState).user?.userInfo as any;
          const enrichedNote: ProviderNote = userInfo
            ? {
                ...createdNote,
                userAccount: {
                  userFirstName: userInfo?.userFirstName,
                  userLastName: userInfo?.userLastName,
                },
              }
            : createdNote;
          dispatch(addProviderNote(enrichedNote));
        } catch {
          dispatch(addProviderNote(createdNote));
        }
      });
  };

export const updateNoteAsync =
  (providerId: number, note: ProviderNote) => (dispatch: AppDispatch) => {
    dispatch(setSavingNote(true));
    dispatch(setSavedNote(false));
    dispatch(setSavingNoteError(null));
    providerService
      .updateProviderNote(providerId, note)
      .pipe(
        catchError((err) => {
          dispatch(setSavingNote(false));
          dispatch(setSavedNote(false));
          dispatch(setSavingNoteError(err.message));
          return throwError(() => new Error(err.message));
        }),
      )
      .subscribe((updatedNote) => {
        dispatch(setSavingNote(false));
        dispatch(setSavedNote(true));
        dispatch(updateProviderNote(updatedNote));
      });
  };

export const deleteNoteAsync =
  (providerId: number, noteId: number) => (dispatch: AppDispatch) => {
    providerService.deleteProviderNote(providerId, noteId).subscribe(() => {
      dispatch(deleteProviderNote({ providerId, noteId }));
    });
  };

export const fetchNoteCommentsAsync =
  (providerId: number, noteId: number) => (dispatch: AppDispatch) => {
    providerService
      .getNoteComments(providerId, noteId)
      // .pipe(
      //   catchError((err) => {
      //     // This should be localized from messageId
      //     dispatch(setSavingError(err.message));
      //     return throwError(() => new Error(err.message));
      //   }),
      // )
      .subscribe((comments) => {
        dispatch(setNoteComments({ noteId, comments }));
      });
  };

export const createNoteCommentAsync =
  (providerId: number, noteId: number, comment: ProviderNoteComment) =>
  (dispatch: AppDispatch, getState: () => RootState) => {
    dispatch(setSavingNote(true));
    dispatch(setSavedNote(false));
    dispatch(setSavingNoteError(null));
    providerService
      .createNoteComment(providerId, noteId, comment)
      // .pipe(
      //   catchError((err) => {
      //     dispatch(setSavingNote(false));
      //     dispatch(setSavedNote(false));
      //     dispatch(setSavingNoteError(err.message));
      //     return throwError(() => new Error(err.message));
      //   }),
      // )
      .subscribe((createdComment) => {
        dispatch(setSavingNote(false));
        dispatch(setSavedNote(true));
        dispatch(setSavingNoteError(null));
        try {
          const userInfo = (getState() as RootState).user?.userInfo as any;
          const enrichedComment: ProviderNoteComment = userInfo
            ? {
                ...createdComment,
                userAccount: {
                  userFirstName: userInfo?.userFirstName,
                  userLastName: userInfo?.userLastName,
                },
              }
            : createdComment;
          dispatch(addNoteComment(enrichedComment));
        } catch {
          dispatch(addNoteComment(createdComment));
        }
      });
  };

export const updateNoteCommentAsync =
  (providerId: number, comment: ProviderNoteComment) =>
  (dispatch: AppDispatch) => {
    dispatch(setSavingNote(true));
    dispatch(setSavedNote(false));
    dispatch(setSavingNoteError(null));
    providerService
      .updateNoteComment(providerId, comment)
      // .pipe(
      //   catchError((err) => {
      //     dispatch(setSavingNote(false));
      //     dispatch(setSavedNote(false));
      //     dispatch(setSavingNoteError(err.message));
      //     return throwError(() => new Error(err.message));
      //   }),
      // )
      .subscribe((updatedComment) => {
        dispatch(setSavingNote(false));
        dispatch(setSavedNote(true));
        dispatch(setSavingNoteError(null));
        dispatch(updateNoteComment(updatedComment));
      });
  };

export const deleteNoteCommentAsync =
  (providerId: number, noteId: number, commentId: number) =>
  (dispatch: AppDispatch) => {
    providerService
      .deleteNoteComment(providerId, noteId, commentId)
      .subscribe(() => {
        dispatch(deleteNoteComment({ providerId, noteId, commentId }));
      });
  };

export const fetchProviderTripPreferencesAsync =
  (providerId: number) => (dispatch: AppDispatch) => {
    dispatch(setLoadingTripPreferences(true));
    dispatch(setLoadingTripPreferencesError(null));

    providerService
      .getTripPreferences(providerId)
      .pipe(
        catchError((err) => {
          dispatch(setLoadingTripPreferences(false));
          dispatch(setLoadingTripPreferencesError(err.message));
          return throwError(() => new Error(err.message));
        }),
      )
      .subscribe((tripPreferences) => {
        dispatch(setLoadingTripPreferences(false));
        dispatch(setLoadingTripPreferencesError(null));
        dispatch(setTripPreferences(tripPreferences));
      });
  };

export const updateTripPreferencesAsync =
  (providerId: number, prefs: BaseTripPreferences) =>
  (dispatch: AppDispatch) => {
    dispatch(setSavingTripPreferences(true));
    dispatch(setSavedTripPreferences(false));
    dispatch(setSavingTripPreferencesError(null));

    providerService
      .updateTripPreferences(providerId, prefs)
      .pipe(
        catchError((err) => {
          dispatch(setSavingTripPreferences(false));
          dispatch(setSavedTripPreferences(false));
          dispatch(
            setSavingTripPreferencesError(
              `${i18next.t('common:preferences.failedToSave')} - ${
                err.message
              }`,
            ),
          );
          return throwError(() => new Error(err.message));
        }),
      )
      .subscribe((updatedPrefs) => {
        dispatch(setTripPreferences(updatedPrefs));
        dispatch(setSavingTripPreferences(false));
        dispatch(setSavedTripPreferences(true));
        dispatch(setSavingTripPreferencesError(null));
      });
  };

export const updateProviderOnboardingUserAsync =
  (transportationProviderUserOnboardingID: number, onboardingUser: Object) =>
  (dispatch: AppDispatch) => {
    dispatch(setSavingOnboardingUser(true));
    dispatch(setSavedOnboardingUser(false));
    providerService
      .updateProviderOnboardingUser(
        transportationProviderUserOnboardingID,
        onboardingUser,
      )
      .pipe(
        catchError((err) => {
          dispatch(setSavingOnboardingUser(false));
          dispatch(setSavedOnboardingUser(false));
          return throwError(() => new Error(err.message));
        }),
      )
      .subscribe((updatedPrefs) => {
        dispatch(setSavingOnboardingUser(false));
        dispatch(setSavedOnboardingUser(true));
      });
  };

export const fetchCoverageAreasAsync = () => (dispatch: AppDispatch) => {
  dispatch(setLoadingCoverageAreas(true));
  dispatch(setLoadedCoverageAreas(false));

  providerService.getCoverageAreas().subscribe({
    next: (result) => {
      dispatch(setLoadingCoverageAreas(false));
      dispatch(setLoadedCoverageAreas(true));
      dispatch(setCoverageAreas(result.coverageAreas));
    },
    error: () => {
      dispatch(setLoadingCoverageAreas(false));
    },
  });
};

export const generateInvoiceAsync =
  (providerId: number, tripLegs: { tripLegID: number }[]) =>
  (dispatch: AppDispatch) => {
    dispatch(setSubmittingInvoice(true));
    dispatch(setSubmittedInvoice(false));
    invoiceService.generateInvoice(providerId, tripLegs).subscribe({
      next: () => {
        dispatch(setSubmittingInvoice(false));
        dispatch(setSubmittedInvoice(true));
      },
      error: (err) => {
        dispatch(setSubmittingInvoice(false));
        dispatch(setSubmittedInvoice(false));
        dispatch(setAttestationError(err.message));
      },
    });
  };

export const attestTier3TripLegAsync =
  (attestations: Tier3TripLegAttestation) => (dispatch: AppDispatch) => {
    dispatch(setSubmittingAttestation(true));
    const providerId = attestations.transportationProviderID;
    dispatch(addAttestationToQueue(String(providerId)));
    providerService.attestTier3TripLeg(attestations).subscribe({
      next: () => {
        dispatch(removeAttestedTripLeg(String(providerId)));
        dispatch(removeAttestationFromQueue(String(providerId)));
      },
      error: (err) => {
        dispatch(removeAttestedTripLeg(String(providerId)));
        dispatch(setAttestationError(err.message));
      },
    });
  };

// This is temporary so don't worry about the hard coding for now
export const fetchBensonProviderIDAsync = () => (dispatch: AppDispatch) => {
  providerService
    .findProviders(1, 1, '', 'Benson', null)
    .subscribe((providerList) => {
      const providerItems = providerList.items?.[0];
      const id = providerItems?.transportationProviderID;
      if (id) {
        dispatch(setBensonProviderID(id));
      }
    });
};

export const getDocumentDownloadInfoAsync =
  (providerID: number, documentID: number) => (dispatch: AppDispatch) => {
    dispatch(setDocumentDownloadInfo(null));
    dispatch(setLoadingDocumentDownloadInfo(true));
    dispatch(setLoadedDocumentDownloadInfo(false));
    providerService.getDocumentDownloadInfo(providerID, documentID).subscribe({
      next: (docInfo) => {
        dispatch(setDocumentDownloadInfo(docInfo));
        dispatch(setLoadedDocumentDownloadInfo(true));
      },
      error: () => {
        dispatch(uiSlice.setErrorMessage('snackbar.documentDownloadError'));
      },
      complete: () => {
        dispatch(setLoadingDocumentDownloadInfo(false));
      },
    });
  };

export const selectSelectedFilter = (state: { provider: ProviderState }) =>
  state.provider.selectedFilter;
export const selectSearchFilter = (state: { provider: ProviderState }) =>
  state.provider.searchFilter;
export const selectSearchDriverIdFilter = (state: {
  provider: ProviderState;
}) => state.provider.searchDriverIdFilter;
export const selectSearchAreaFilter = (state: { provider: ProviderState }) =>
  state.provider.searchAreaFilter;
export const selectDriverSearchFilter = (state: { provider: ProviderState }) =>
  state.provider.driverSearchFilter;
export const selectVehicleSearchFilter = (state: { provider: ProviderState }) =>
  state.provider.vehicleSearchFilter;
export const selectPage = (state: { provider: ProviderState }) =>
  state.provider.page;
export const selectPageSize = (state: { provider: ProviderState }) =>
  state.provider.pageSize;
export const selectSortModel = (state: { provider: ProviderState }) =>
  state.provider.sortModel;
export const selectLoadingProviderList = (state: { provider: ProviderState }) =>
  state.provider.loadingProviderList;
export const selectLoadedProviderList = (state: { provider: ProviderState }) =>
  state.provider.loadedProviderList;
export const selectProviderList = (state: { provider: ProviderState }) =>
  state.provider.providerList;

// export const selectTier3SelectedFilter = (state: { provider: ProviderState }) =>
//   state.provider.tier3SelectedFilter;
// export const selectTier3SearchFilter = (state: { provider: ProviderState }) =>
//   state.provider.tier3SearchFilter;
// export const selectTier3Page = (state: { provider: ProviderState }) =>
//   state.provider.tier3Page;
// export const selectTier3PageSize = (state: { provider: ProviderState }) =>
//   state.provider.tier3PageSize;
// export const selectTier3SortModel = (state: { provider: ProviderState }) =>
//   state.provider.tier3SortModel;
// export const selectLoadingTier3ProviderList = (state: {
//   provider: ProviderState;
// }) => state.provider.loadingTier3ProviderList;
// export const selectLoadedTier3ProviderList = (state: {
//   provider: ProviderState;
// }) => state.provider.loadedTier3ProviderList;
// export const selectTier3ProviderList = (state: { provider: ProviderState }) =>
//   state.provider.tier3ProviderList;
// export const selectTier3Provider = (state: { provider: ProviderState }) =>
//   state.provider.tier3Provider;

export const selectLoadingProvider = (state: { provider: ProviderState }) =>
  state.provider.loadingProvider;
export const selectLoadedProvider = (state: { provider: ProviderState }) =>
  state.provider.loadedProvider;
export const selectLoadingProviderError = (state: {
  provider: ProviderState;
}) => state.provider.loadingProviderError;
export const selectSavingProvider = (state: { provider: ProviderState }) =>
  state.provider.savingProvider;
export const selectSavedProvider = (state: { provider: ProviderState }) =>
  state.provider.savedProvider;
export const selectSavingError = (state: { provider: ProviderState }) =>
  state.provider.savingError;
export const selectSavingSuccess = (state: { provider: ProviderState }) =>
  state.provider.savingSuccess;
export const selectProvider = (state: { provider: ProviderState }) =>
  state.provider.provider;
export const selectProviderDocuments = (state: { provider: ProviderState }) =>
  state.provider.providerDocuments;
export const selectLoadingProviderDocuments = (state: {
  provider: ProviderState;
}) => state.provider.loadingProviderDocuments;
export const selectUploadingDocument = (state: { provider: ProviderState }) =>
  state.provider.uploadingDocument;
export const selectUploadedDocument = (state: { provider: ProviderState }) =>
  state.provider.uploadedDocument;
export const selectUploadError = (state: { provider: ProviderState }) =>
  state.provider.uploadError;
export const selectProviderNotes = (state: { provider: ProviderState }) =>
  state.provider.providerNotes;
export const selectSavingNote = (state: { provider: ProviderState }) =>
  state.provider.savingNote;
export const selectSavedNote = (state: { provider: ProviderState }) =>
  state.provider.savedNote;
export const selectSavingNoteError = (state: { provider: ProviderState }) =>
  state.provider.savingNoteError;
export const selectTabsetOneIndex = (state: { provider: ProviderState }) =>
  state.provider.tabsetOneIndex;
export const selectTabsetTwoIndex = (state: { provider: ProviderState }) =>
  state.provider.tabsetTwoIndex;
export const selectLoadingTripPreferences = (state: {
  provider: ProviderState;
}) => state.provider.loadingTripPreferences;
export const selectLoadingTripPreferencesError = (state: {
  provider: ProviderState;
}) => state.provider.loadingTripPreferencesError;

export const selectSavingTripPreferences = (state: {
  provider: ProviderState;
}) => state.provider.savingTripPreferences;
export const selectSavingTripPreferencesError = (state: {
  provider: ProviderState;
}) => state.provider.savingTripPreferencesError;
export const selectSavedTripPreferences = (state: {
  provider: ProviderState;
}) => state.provider.savedTripPreferences;
export const selectTripPreferences = (state: { provider: ProviderState }) =>
  state.provider.tripPreferences;
export const selectSavingOnboardingUser = (state: {
  provider: ProviderState;
}) => state.provider.savingOnboardingUser;
export const selectSavedOnboardingUser = (state: { provider: ProviderState }) =>
  state.provider.savedOnboardingUser;
export const selectCoverageAreas = (state: { provider: ProviderState }) =>
  state.provider.coverageAreas;
export const selectLoadingCoverageAreas = (state: {
  provider: ProviderState;
}) => state.provider.loadingCoverageAreas;
export const selectLoadedCoverageAreas = (state: { provider: ProviderState }) =>
  state.provider.loadedCoverageAreas;
export const selectTripLegsToAttest = (state: { provider: ProviderState }) =>
  state.provider.tripLegsToAttest;
export const selectLoadingTripLegsToAttest = (state: {
  provider: ProviderState;
}) => state.provider.loadingTripLegsToAttest;
export const selectLoadedTripLegsToAttest = (state: {
  provider: ProviderState;
}) => state.provider.loadedTripLegsToAttest;
export const selectAttestationQueue = (state: { provider: ProviderState }) =>
  state.provider.attestationQueue;
export const selectAttestationQueueLength = (state: {
  provider: ProviderState;
}) => state.provider.attestationQueue.length;
export const selectSubmittingAttestation = (state: {
  provider: ProviderState;
}) => state.provider.submittingAttestation;
export const selectSubmittedAttestation = (state: {
  provider: ProviderState;
}) => state.provider.submittedAttestation;
export const selectSubmittingInvoice = (state: { provider: ProviderState }) =>
  state.provider.submittingInvoice;
export const selectSubmittedInvoice = (state: { provider: ProviderState }) =>
  state.provider.submittedInvoice;
export const selectBensonProviderID = (state: { provider: ProviderState }) =>
  state.provider.bensonProviderID;
export const selectAttestationError = (state: { provider: ProviderState }) =>
  state.provider.attestationError;
export const selectDocumentDownloadInfo = (state: {
  provider: ProviderState;
}) => state.provider.documentDownloadInfo;
export const selectLoadingDocumentDownloadInfo = (state: {
  provider: ProviderState;
}) => state.provider.loadingDocumentDownloadInfo;
export const selectLoadedDocumentDownloadInfo = (state: {
  provider: ProviderState;
}) => state.provider.loadedDocumentDownloadInfo;

export default slice.reducer;
